import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Microphone, MicrophoneSlash, SubtitlesSlash, User } from "@phosphor-icons/react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { useIncomingCallStatus } from "@/hooks/useIncomingCallStatus"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { startTranscriptionFavicon, stopTranscriptionFavicon } from "@/lib/transcription-live-indicator"
import { cn } from "@/lib/utils"
import { MicrophoneSelectorPopover } from "@/modules/telephony/components/microphone-selector-popover"
import { usePhoneCallTranscription } from "@/modules/telephony/hooks/usePhoneCallTranscription"
import { usePhoneCallTranscriptionStore } from "@/stores/usePhoneCallTranscriptionStore"

export const PhoneCallTranscriptionBarContainer = () => {
  const { t } = useTranslation()
  const isSpeechToTextEnabled = useIsFeatureEnabled("SPEECH_TO_TEXT")

  const isRecording = usePhoneCallTranscriptionStore((state) => state.isRecording)
  const patientName = usePhoneCallTranscriptionStore((state) => state.patientName)
  const currentAppointmentId = usePhoneCallTranscriptionStore((state) => state.currentAppointmentId)
  const currentProgressNoteId = usePhoneCallTranscriptionStore((state) => state.currentProgressNoteId)
  const storeLanguage = usePhoneCallTranscriptionStore((state) => state.language)
  const stopRecording = usePhoneCallTranscriptionStore((state) => state.stopRecording)

  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([])

  const { stopTranscription, selectedMicrophoneId, setSelectedMicrophoneId } = usePhoneCallTranscription({
    appointmentId: currentAppointmentId ?? "",
    progressNoteId: currentProgressNoteId,
    language: storeLanguage ?? "ENGLISH",
    enabled: isSpeechToTextEnabled,
  })

  // Any broadcast means another transcription just started and took the shared microphone.
  useIncomingCallStatus(() => {
    stopTranscription()
  })

  // Load available microphones
  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Request microphone permission first
        await navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
          stream.getTracks().forEach((track) => track.stop())
        })

        const devices = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devices.filter(
          (device, index, self) =>
            device.kind === "audioinput" &&
            device.label !== "" &&
            self.findIndex((d) => d.groupId === device.groupId) === index,
        )
        setAvailableDevices(audioInputs)

        // Set default device if none selected
        if (!selectedMicrophoneId && audioInputs.length > 0) {
          setSelectedMicrophoneId(audioInputs[0].deviceId)
        }
      } catch (error) {
        console.error("[PhoneCallTranscriptionBarContainer] Failed to load microphones:", error)
      }
    }

    if (isRecording) {
      loadDevices()
    }
  }, [isRecording, selectedMicrophoneId, setSelectedMicrophoneId])

  useEffect(() => {
    if (isRecording) startTranscriptionFavicon()
    else stopTranscriptionFavicon()
    // Stop the pulsing favicon (and restore the original) if the bar unmounts while recording.
    return () => stopTranscriptionFavicon()
  }, [isRecording])

  const handleSelectDevice = (deviceId: string) => {
    setSelectedMicrophoneId(deviceId)
  }

  const handleStopTranscription = async () => {
    await stopTranscription()
    stopRecording()
  }

  if (!isSpeechToTextEnabled || !isRecording) {
    return null
  }

  const displayName = patientName ?? "Patient"

  return (
    <div
      className={cn(
        "border-border bg-surface fixed bottom-0 left-26 z-50 flex w-120 items-center gap-2 rounded-t-xl border-t border-r border-l px-4 py-2 pr-2",
        "shadow-bar",
      )}>
      <Avatar className="h-6 w-6">
        <AvatarFallback className="bg-ochre-surface text-on-ochre-surface text-xs">
          <User />
        </AvatarFallback>
      </Avatar>

      <span className="text-on-surface flex-1 truncate text-sm">{displayName}</span>

      <div className="flex items-center gap-2">
        <Button
          onClick={handleStopTranscription}
          className="bg-critical text-on-critical hover:bg-critical/90 flex h-auto items-center gap-0 rounded-3xl px-4 py-2 text-sm font-medium transition-all">
          <SubtitlesSlash size={24} weight="regular" />
          <span className="px-2 py-0.5">{t("telephony.transcription.endTranscription")}</span>
        </Button>

        <MicrophoneSelectorPopover
          selectedDeviceId={selectedMicrophoneId}
          onSelectDevice={handleSelectDevice}
          availableDevices={availableDevices}>
          <button
            className="text-on-surface-subdued hover:text-on-surface flex items-center transition-all"
            aria-label={t("telephony.transcription.selectMicrophone")}>
            <span className="flex h-10 w-10 items-center justify-center">
              {availableDevices.length > 0 ? (
                <Microphone size={24} weight="regular" />
              ) : (
                <MicrophoneSlash size={24} weight="regular" />
              )}
            </span>
            <span className="flex h-8 w-8 items-center justify-center">
              <CaretDown size={24} weight="regular" />
            </span>
          </button>
        </MicrophoneSelectorPopover>
      </div>
    </div>
  )
}
